import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { supabase, edgeFunctionErrorBody } from '@/lib/supabase';
import { fmtDataHoraLocal } from '@/pages/plan/shared';

/**
 * Liberações provisórias de acesso do cliente (P-46).
 *
 * Mostra o histórico — desbloqueios em confiança, com o comprovante, e
 * liberações manuais, com o comentário — e permite ao superadmin liberar o
 * acesso com um comentário obrigatório. A liberação é gravada pela Edge
 * Function `liberar-acesso`; a tabela não aceita escrita pela API.
 */

const BUCKET = 'comprovantes-pagamento';

interface Liberacao {
  id: string;
  origem: 'cliente' | 'superadmin';
  stripe_invoice_id: string | null;
  comprovante_caminho: string | null;
  comentario: string | null;
  expira_em: string;
  criado_em: string;
}

const ORIGEM: Record<Liberacao['origem'], string> = {
  cliente: 'Desbloqueio em confiança',
  superadmin: 'Liberação manual',
};

interface Props {
  profileId: string;
  accountId: string | null;
  /** Assinatura já ativa: liberar não faz sentido, só o histórico aparece. */
  assinaturaAtiva: boolean;
}

export default function LiberacoesDeAcesso({ profileId, accountId, assinaturaAtiva }: Props) {
  const [liberacoes, setLiberacoes] = useState<Liberacao[]>([]);
  const [horas, setHoras] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [agora, setAgora] = useState(() => Date.now());

  const carregar = useCallback(async () => {
    if (!accountId) {
      setCarregando(false);
      return;
    }
    setCarregando(true);
    const [{ data: linhas }, { data: parametros }] = await Promise.all([
      supabase
        .from('liberacoes_acesso')
        .select(
          'id, origem, stripe_invoice_id, comprovante_caminho, comentario, expira_em, criado_em',
        )
        .eq('account_id', accountId)
        .is('deletado_em', null)
        .order('criado_em', { ascending: false }),
      supabase
        .from('parametros_cobranca')
        .select('horas_desbloqueio_confianca')
        .eq('id', true)
        .maybeSingle(),
    ]);
    setLiberacoes((linhas as Liberacao[] | null) ?? []);
    setHoras((parametros?.horas_desbloqueio_confianca as number | undefined) ?? null);
    setAgora(Date.now());
    setCarregando(false);
  }, [accountId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const abrirComprovante = async (caminho: string) => {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(caminho, 600);
    if (error || !data?.signedUrl) {
      setErro('Não foi possível abrir o comprovante.');
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const liberar = async (e: FormEvent) => {
    e.preventDefault();
    const texto = comentario.trim();
    if (!texto) {
      setErro('Informe o motivo da liberação.');
      return;
    }
    setEnviando(true);
    setErro(null);
    setSucesso(null);

    try {
      const { data, error } = await supabase.functions.invoke('liberar-acesso', {
        body: { acao: 'liberar_manual', profileId, comentario: texto },
      });
      if (error || !data?.liberado) {
        const corpo = error ? await edgeFunctionErrorBody(error) : null;
        setErro(corpo?.error || 'Não foi possível liberar o acesso.');
        return;
      }
      setComentario('');
      setSucesso(`Acesso liberado até ${fmtDataHoraLocal(data.expiraEm as string)}.`);
      await carregar();
    } catch {
      setErro('Não foi possível liberar o acesso.');
    } finally {
      setEnviando(false);
    }
  };

  if (!accountId) {
    return (
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-sm text-foreground-500">
        Cliente sem conta vinculada.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {!assinaturaAtiva && (
        <form
          onSubmit={liberar}
          className="bg-background-50 border border-background-200/70 rounded-lg p-5 flex flex-col gap-3"
        >
          <div>
            <h4 className="text-sm font-semibold text-foreground-900">Liberar acesso</h4>
            <p className="text-xs text-foreground-500 mt-1">
              Libera o uso da plataforma{horas ? ` por ${horas} horas` : ''} sem alterar a
              assinatura. Use quando o pagamento foi feito e ainda não foi confirmado pelo banco.
            </p>
          </div>
          <div>
            <label
              htmlFor="comentario-liberacao"
              className="block text-xs font-medium text-foreground-700 mb-1"
            >
              Comentário (obrigatório)
            </label>
            <textarea
              id="comentario-liberacao"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              maxLength={1000}
              rows={3}
              required
              placeholder="Ex.: cliente enviou comprovante por e-mail; boleto pago em 01/10, aguardando compensação."
              className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400"
            />
          </div>
          {erro && (
            <p role="alert" className="text-xs text-red-700">
              {erro}
            </p>
          )}
          {sucesso && (
            <p role="status" className="text-xs text-green-700">
              {sucesso}
            </p>
          )}
          <div>
            <button
              type="submit"
              disabled={enviando || !comentario.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
            >
              {enviando ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Liberando...
                </>
              ) : (
                <>
                  <i className="ri-lock-unlock-line"></i>
                  Liberar acesso{horas ? ` por ${horas} horas` : ''}
                </>
              )}
            </button>
          </div>
        </form>
      )}

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-background-200/70">
          <h4 className="text-sm font-semibold text-foreground-900">Histórico de liberações</h4>
        </div>
        {carregando ? (
          <div className="p-6 text-sm text-foreground-500 flex items-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i>
            Carregando...
          </div>
        ) : liberacoes.length === 0 ? (
          <p className="p-6 text-sm text-foreground-500">Nenhuma liberação registrada.</p>
        ) : (
          <ul className="divide-y divide-background-200/50">
            {liberacoes.map((l) => {
              const vigente = new Date(l.expira_em).getTime() > agora;
              return (
                <li
                  key={l.id}
                  className="px-5 py-3 flex flex-col sm:flex-row sm:items-start justify-between gap-2"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-foreground-800">
                        {ORIGEM[l.origem]}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                          vigente
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-background-100 text-foreground-500 border-background-200'
                        }`}
                      >
                        {vigente ? 'Vigente' : 'Expirada'}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {fmtDataHoraLocal(l.criado_em)} → até {fmtDataHoraLocal(l.expira_em)}
                      {l.stripe_invoice_id ? ` · fatura ${l.stripe_invoice_id}` : ''}
                    </p>
                    {l.comentario && (
                      <p className="text-xs text-foreground-700 mt-1 break-words">{l.comentario}</p>
                    )}
                  </div>
                  {l.comprovante_caminho && (
                    <button
                      type="button"
                      onClick={() => abrirComprovante(l.comprovante_caminho as string)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-file-text-line"></i>
                      Ver comprovante
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
